/**
 * INVOICE PRIORITIZATION SERVICE - PHASE 3
 * Intelligent prioritization for "which should I chase first?" queries
 *
 * Prioritises invoices based on:
 * 1. Impact (Amount) - Higher value = higher priority
 * 2. Urgency (Days Overdue) - Longer overdue = more urgent
 * 3. Combination - Weighted score
 */

// PRIORITIZATION CONFIGURATION
const PRIORITY_CONFIG = {
  // Weighting: Amount is more important than urgency
  // 60% weight on invoice amount (direct cash impact)
  // 40% weight on days overdue (relationship risk and urgency)
  AMOUNT_WEIGHT: 0.6,
  URGENCY_WEIGHT: 0.4,

  // Score scale: 0-100 for easy percentage interpretation
  MAX_SCORE: 100,
  NEUTRAL_SCORE: 50, // Used when all invoices have equal values

  // Priority thresholds based on score (0-100 scale)
  // High: Top 30% of invoices (score >= 70)
  // Medium: Middle 30% (score 40-69)
  // Low: Bottom 40% (score < 40)
  HIGH_PRIORITY_THRESHOLD: 70,
  MEDIUM_PRIORITY_THRESHOLD: 40,

  // Reasoning thresholds for human-readable descriptions
  HIGHEST_VALUE_THRESHOLD: 80, // Top 20% - "Highest value"
  HIGH_VALUE_THRESHOLD: 60, // Top 40% - "High value"
  MODERATE_VALUE_THRESHOLD: 40, // Top 60% - "Moderate value"

  // Days overdue thresholds for highlighting urgency
  VERY_OVERDUE_DAYS: 20, // 3 weeks - significant relationship risk
  OVERDUE_DAYS: 10, // 2 weeks - standard payment terms exceeded

  // Amount threshold for "large invoice" flag
  LARGE_INVOICE_THRESHOLD: 20000, // £20k - significant cash flow impact
};

export interface PrioritizedInvoice {
  invoice: any;
  priority: "high" | "medium" | "low";
  score: number;
  reasoning: string;
}

export interface PrioritizationResult {
  prioritized: PrioritizedInvoice[];
  totalAmount: number;
  summary: string;
}

/**
 * Prioritise invoices for chasing
 */
export function prioritizeInvoices(invoices: any[]): PrioritizationResult {
  if (!invoices || invoices.length === 0) {
    return {
      prioritized: [],
      totalAmount: 0,
      summary: "No invoices to prioritise",
    };
  }

  // Calculate priority scores
  const scored = invoices.map((invoice) => {
    const amountScore = calculateAmountScore(invoice.amount, invoices);
    const urgencyScore = calculateUrgencyScore(invoice.daysOverdue, invoices);

    // Weighted combination using configured weights
    const totalScore =
      amountScore * PRIORITY_CONFIG.AMOUNT_WEIGHT +
      urgencyScore * PRIORITY_CONFIG.URGENCY_WEIGHT;

    const priority = determinePriority(totalScore);
    const reasoning = generateReasoning(
      invoice,
      amountScore,
      urgencyScore,
      invoices,
    );

    return {
      invoice,
      priority,
      score: totalScore,
      reasoning,
    };
  });

  // Sort by score (highest first)
  const prioritized = scored.sort((a, b) => b.score - a.score);

  // Calculate total
  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);

  // Generate summary
  const highPriority = prioritized.filter((p) => p.priority === "high").length;
  const summary = `${highPriority} high priority invoice${highPriority !== 1 ? "s" : ""} to chase`;

  return {
    prioritized,
    totalAmount,
    summary,
  };
}

/**
 * Calculate amount score (0-100)
 * Higher amounts get higher scores
 */
function calculateAmountScore(amount: number, allInvoices: any[]): number {
  const amounts = allInvoices.map((inv) => inv.amount);
  const maxAmount = Math.max(...amounts);
  const minAmount = Math.min(...amounts);

  if (maxAmount === minAmount) {
    return PRIORITY_CONFIG.NEUTRAL_SCORE; // All invoices have equal priority
  }

  // Normalize to configured score scale
  return (
    ((amount - minAmount) / (maxAmount - minAmount)) * PRIORITY_CONFIG.MAX_SCORE
  );
}

/**
 * Calculate urgency score (0-100)
 * More days overdue = higher score
 */
function calculateUrgencyScore(
  daysOverdue: number,
  allInvoices: any[],
): number {
  const days = allInvoices.map((inv) => inv.daysOverdue);
  const maxDays = Math.max(...days);
  const minDays = Math.min(...days);

  if (maxDays === minDays) {
    return PRIORITY_CONFIG.NEUTRAL_SCORE; // All invoices equally overdue
  }

  // Normalize to configured score scale
  return (
    ((daysOverdue - minDays) / (maxDays - minDays)) * PRIORITY_CONFIG.MAX_SCORE
  );
}

/**
 * Determine priority level based on score
 */
function determinePriority(score: number): "high" | "medium" | "low" {
  if (score >= PRIORITY_CONFIG.HIGH_PRIORITY_THRESHOLD) return "high";
  if (score >= PRIORITY_CONFIG.MEDIUM_PRIORITY_THRESHOLD) return "medium";
  return "low";
}

/**
 * Generate human-readable reasoning for priority
 */
function generateReasoning(
  invoice: any,
  amountScore: number,
  urgencyScore: number,
  allInvoices: any[],
): string {
  const reasons: string[] = [];

  // Amount reasoning
  if (amountScore >= PRIORITY_CONFIG.HIGHEST_VALUE_THRESHOLD) {
    reasons.push("Highest value");
  } else if (amountScore >= PRIORITY_CONFIG.HIGH_VALUE_THRESHOLD) {
    reasons.push("High value");
  } else if (amountScore >= PRIORITY_CONFIG.MODERATE_VALUE_THRESHOLD) {
    reasons.push("Moderate value");
  }

  // Urgency reasoning
  if (urgencyScore >= PRIORITY_CONFIG.HIGHEST_VALUE_THRESHOLD) {
    reasons.push("Very overdue");
  } else if (urgencyScore >= PRIORITY_CONFIG.HIGH_VALUE_THRESHOLD) {
    reasons.push("Significantly overdue");
  } else if (urgencyScore >= PRIORITY_CONFIG.MODERATE_VALUE_THRESHOLD) {
    reasons.push("Moderately overdue");
  }

  // Specific insights
  if (invoice.daysOverdue > PRIORITY_CONFIG.VERY_OVERDUE_DAYS) {
    reasons.push(`${invoice.daysOverdue} days overdue`);
  } else if (invoice.daysOverdue > PRIORITY_CONFIG.OVERDUE_DAYS) {
    reasons.push(`${invoice.daysOverdue} days overdue`);
  }

  if (invoice.amount > PRIORITY_CONFIG.LARGE_INVOICE_THRESHOLD) {
    reasons.push("Large amount");
  }

  // Combine reasoning
  if (reasons.length === 0) {
    return "Lower priority";
  }

  return reasons.join(" • ");
}

/**
 * Get priority emoji
 */
export function getPriorityEmoji(priority: "high" | "medium" | "low"): string {
  switch (priority) {
    case "high":
      return "🔴";
    case "medium":
      return "🟡";
    case "low":
      return "🟢";
  }
}

/**
 * Get priority label
 */
export function getPriorityLabel(priority: "high" | "medium" | "low"): string {
  switch (priority) {
    case "high":
      return "HIGH PRIORITY";
    case "medium":
      return "MEDIUM PRIORITY";
    case "low":
      return "LOW PRIORITY";
  }
}
